import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPublicDepartmentNames, submitProjectSetupRequest } from '../services/projectSetupRequestApi';
import { AuthLayout } from '../components/layout/AuthLayout';
import { AuthTextField } from '../components/auth/AuthTextField';
import { AuthSelectField } from '../components/auth/AuthSelectField';
import { AuthTextarea } from '../components/auth/AuthTextarea';
import { AuthStepIndicator } from '../components/auth/AuthStepIndicator';
import { AuthSubmitButton } from '../components/auth/AuthSubmitButton';
import { AuthCardFooter } from '../components/auth/AuthCardFooter';

const BLOCKED_EMAIL_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
const STEP_LABELS = ['Company', 'Project', 'Contact'] as const;

type Step = 1 | 2 | 3;

type RequestForm = {
  companyName: string;
  companyInfo: string;
  companyContactInfo: string;
  industry: string;
  companySize: string;
  engagementGoal: string;
  additionalContext: string;
  projectName: string;
  departmentName: string;
  projectCreationDate: string;
  requesterEmail: string;
};

const todayIso = () => new Date().toISOString().split('T')[0];

const initialForm = (): RequestForm => ({
  companyName: '',
  companyInfo: '',
  companyContactInfo: '',
  industry: '',
  companySize: '',
  engagementGoal: '',
  additionalContext: '',
  projectName: '',
  departmentName: '',
  projectCreationDate: todayIso(),
  requesterEmail: '',
});

function validateWorkEmail(email: string) {
  const trimmed = email.trim();
  const at = trimmed.lastIndexOf('@');
  if (at < 0) return 'Please enter a valid work email address.';
  const domain = trimmed.slice(at + 1).toLowerCase();
  if (BLOCKED_EMAIL_DOMAINS.includes(domain)) {
    return 'Please use your company work email, not a personal email provider.';
  }
  return '';
}

function validateStep(form: RequestForm, currentStep: Step) {
  if (currentStep === 1) {
    if (!form.companyName.trim()) return 'Company name is required.';
    if (!form.companyInfo.trim()) return 'Company info is required.';
    if (!form.companyContactInfo.trim()) return 'Company contact info is required.';
  }
  if (currentStep === 2) {
    if (!form.projectName.trim()) return 'Project name is required.';
    if (!form.departmentName.trim()) return 'Department name is required.';
    if (!form.projectCreationDate) return 'Project creation date is required.';
  }
  if (currentStep === 3) {
    if (!form.requesterEmail.trim()) return 'Your work email is required.';
    const emailError = validateWorkEmail(form.requesterEmail);
    if (emailError) return emailError;
  }
  return '';
}

function validateAllSteps(form: RequestForm): { error: string; step: Step } | null {
  for (const stepNumber of [1, 2, 3] as Step[]) {
    const error = validateStep(form, stepNumber);
    if (error) return { error, step: stepNumber };
  }
  return null;
}

function RequestProject() {
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<RequestForm>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [departmentNames, setDepartmentNames] = useState<string[]>([]);

  // Falls back to a free-text field when the list is empty or unavailable.
  useEffect(() => {
    let cancelled = false;
    getPublicDepartmentNames()
      .then((names) => {
        if (!cancelled) setDepartmentNames(names);
      })
      .catch(() => {
        if (!cancelled) setDepartmentNames([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updateField = (name: keyof RequestForm, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrorMessage('');
  };

  const goToStep = (targetStep: number) => {
    setErrorMessage('');
    setStep(targetStep as Step);
  };

  const goNext = () => {
    const error = validateStep(form, step);
    if (error) {
      setErrorMessage(error);
      return;
    }
    setStep((prev) => (prev < 3 ? ((prev + 1) as Step) : prev));
  };

  const goBack = () => {
    setErrorMessage('');
    setStep((prev) => (prev > 1 ? ((prev - 1) as Step) : prev));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const validation = validateAllSteps(form);
    if (validation) {
      setErrorMessage(validation.error);
      setStep(validation.step);
      return;
    }

    setSubmitting(true);
    setErrorMessage('');

    try {
      await submitProjectSetupRequest({
        requesterEmail: form.requesterEmail.trim(),
        companyName: form.companyName.trim(),
        companyInfo: form.companyInfo.trim(),
        companyContactInfo: form.companyContactInfo.trim(),
        industry: form.industry.trim(),
        companySize: form.companySize.trim(),
        engagementGoal: form.engagementGoal.trim(),
        additionalContext: form.additionalContext.trim(),
        projectName: form.projectName.trim(),
        departmentName: form.departmentName.trim(),
        projectCreationDate: form.projectCreationDate,
        smeUserIds: [],
      });
      setSubmitted(true);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      wide
      compact
      title="Request a project"
      subtitle="Tell us about your company and project. We will review your request and email you an invitation if approved."
    >
      {submitted ? (
        <div className="auth-success-panel">
          <h2 className="auth-success-panel__title">Request submitted</h2>
          <p className="auth-success-panel__text">
            Thank you. We received your project setup request and will email you at{' '}
            <strong>{form.requesterEmail}</strong> if it is approved.
          </p>
          <Link to="/signin" className="auth-link">
            Return to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="auth-form">
          <AuthStepIndicator steps={[...STEP_LABELS]} currentStep={step} onStepClick={goToStep} />

          {errorMessage ? <p className="auth-form__error">{errorMessage}</p> : null}

          {step === 1 ? (
            <div className="auth-request-grid">
              <AuthTextField
                id="companyName"
                label="Company name"
                value={form.companyName}
                onChange={(event) => updateField('companyName', event.target.value)}
                placeholder="Enter company name"
                required
              />
              <AuthTextField
                id="industry"
                label="Industry"
                value={form.industry}
                onChange={(event) => updateField('industry', event.target.value)}
                placeholder="e.g. Financial services"
              />
              <div className="auth-request-grid__full">
                <AuthTextarea
                  id="companyInfo"
                  label="Company info"
                  value={form.companyInfo}
                  onChange={(event) => updateField('companyInfo', event.target.value)}
                  placeholder="Brief description of your company"
                  rows={3}
                  required
                />
              </div>
              <div className="auth-request-grid__full">
                <AuthTextarea
                  id="companyContactInfo"
                  label="Company contact info"
                  value={form.companyContactInfo}
                  onChange={(event) => updateField('companyContactInfo', event.target.value)}
                  placeholder="Address, phone, or other contact details"
                  rows={2}
                  required
                />
              </div>
              <AuthTextField
                id="companySize"
                label="Company size"
                value={form.companySize}
                onChange={(event) => updateField('companySize', event.target.value)}
                placeholder="e.g. 500–1,000 employees"
              />
              <div className="auth-request-grid__full">
                <AuthTextarea
                  id="engagementGoal"
                  label="Engagement goal"
                  value={form.engagementGoal}
                  onChange={(event) => updateField('engagementGoal', event.target.value)}
                  placeholder="What do you want to achieve with this engagement?"
                  rows={2}
                />
              </div>
              <div className="auth-request-grid__full">
                <AuthTextarea
                  id="additionalContext"
                  label="Additional context"
                  value={form.additionalContext}
                  onChange={(event) => updateField('additionalContext', event.target.value)}
                  placeholder="Any additional background or organizational context"
                  rows={2}
                />
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="auth-request-grid">
              <AuthTextField
                id="projectName"
                label="Project name"
                value={form.projectName}
                onChange={(event) => updateField('projectName', event.target.value)}
                placeholder="Enter project name"
                required
              />
              {departmentNames.length > 0 ? (
                <AuthSelectField
                  id="departmentName"
                  label="Department"
                  value={form.departmentName}
                  onChange={(event) => updateField('departmentName', event.target.value)}
                  options={departmentNames.map((name) => ({ value: name, label: name }))}
                  placeholder="Select a department"
                  required
                />
              ) : (
                <AuthTextField
                  id="departmentName"
                  label="Department name"
                  value={form.departmentName}
                  onChange={(event) => updateField('departmentName', event.target.value)}
                  placeholder="Enter department name"
                  autoComplete="organization-title"
                  required
                />
              )}
              <AuthTextField
                id="projectCreationDate"
                label="Project creation date"
                type="date"
                value={form.projectCreationDate}
                onChange={(event) => updateField('projectCreationDate', event.target.value)}
                required
              />
            </div>
          ) : null}

          {step === 3 ? (
            <>
              <div className="auth-summary">
                <p>
                  <strong>Company:</strong> {form.companyName || '—'}
                </p>
                {form.industry ? (
                  <p>
                    <strong>Industry:</strong> {form.industry}
                  </p>
                ) : null}
                <p>
                  <strong>Project:</strong> {form.projectName || '—'}
                </p>
                <p>
                  <strong>Department:</strong> {form.departmentName || '—'}
                </p>
              </div>
              <AuthTextField
                id="requesterEmail"
                label="Your work email"
                type="email"
                value={form.requesterEmail}
                onChange={(event) => updateField('requesterEmail', event.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                required
              />
            </>
          ) : null}

          <div className="auth-form__actions">
            {step > 1 ? (
              <button type="button" className="auth-btn auth-btn--secondary" onClick={goBack}>
                Back
              </button>
            ) : (
              <span />
            )}
            {step < 3 ? (
              <button type="button" className="auth-btn auth-btn--primary" onClick={goNext}>
                Continue
              </button>
            ) : (
              <AuthSubmitButton loading={submitting} label="Submit request" />
            )}
          </div>

          <AuthCardFooter>
            Already have an account?{' '}
            <Link to="/signin" className="auth-link">
              Sign in
            </Link>
          </AuthCardFooter>
        </form>
      )}
    </AuthLayout>
  );
}

export default RequestProject;
